import React, {useEffect, useState} from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {AuthService} from '../service/auth.service';
import {Alert, Button, Card, Form, Input, Layout, Typography} from 'antd';
import {LockOutlined} from '@ant-design/icons';
import {useTheme} from '../components/ThemeProvider';

const {Content} = Layout;
const {Title, Text} = Typography;

const ResetPasswordPage: React.FC = () => {
    const [form] = Form.useForm();
    const [token, setToken] = useState('');
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);
    const location = useLocation();
    const navigate = useNavigate();
    const {isDark} = useTheme();

    useEffect(() => {
        const queryParams = new URLSearchParams(location.search);
        const tokenParam = queryParams.get('token');
        if (tokenParam) {
            setToken(tokenParam);
        } else {
            setError('Reset token is missing. Please check your reset link.');
        }
    }, [location]);

    const onFinish = async (values: { password: string; confirmPassword: string }) => {
        if (values.password !== values.confirmPassword) {
            setError('Passwords do not match');
            return;
        }

        try {
            setLoading(true);
            setError('');
            await AuthService.resetPassword(token, values.password);
            setSuccess(true);
        } catch (err: any) {
            setError(err.response?.data?.message || err.message || 'Failed to reset password. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Layout style={{minHeight: '100vh', backgroundColor: isDark ? '#141414' : '#f0f2f5'}}>
            <Content style={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                padding: '24px'
            }}>
                <Card
                    variant="borderless"
                    style={{
                        width: '100%',
                        maxWidth: '450px',
                        borderRadius: '8px',
                        backgroundColor: isDark ? '#1f1f1f' : '#fff',
                    }}
                    styles={{body: {padding: '32px'}}}
                >
                    <div style={{textAlign: 'center', marginBottom: '24px'}}>
                        <Title level={3}>Reset Your Password</Title>
                        <Text type="secondary">Enter your new password below</Text>
                    </div>

                    {error && <Alert style={{marginBottom: '16px'}} message={error} type="error" showIcon/>}

                    {success ? (
                        <Alert
                            message="Password Reset Successfully"
                            description="Your password has been reset. You can close this page and log in with your new password in the app."
                            type="success"
                            showIcon
                            style={{marginBottom: '16px'}}
                        />
                    ) : (
                        <Form
                            form={form}
                            onFinish={onFinish}
                            layout="vertical"
                            size="large"
                        >
                            <Form.Item
                                name="password"
                                rules={[
                                    {required: true, message: 'Please enter a new password'},
                                    {min: 6, message: 'Password must be at least 6 characters'},
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined/>}
                                    placeholder="New password"
                                />
                            </Form.Item>

                            <Form.Item
                                name="confirmPassword"
                                rules={[
                                    {required: true, message: 'Please confirm your password'},
                                ]}
                            >
                                <Input.Password
                                    prefix={<LockOutlined/>}
                                    placeholder="Confirm new password"
                                />
                            </Form.Item>

                            <Form.Item style={{marginTop: '24px'}}>
                                <Button
                                    type="primary"
                                    htmlType="submit"
                                    block
                                    loading={loading}
                                    disabled={!token}
                                >
                                    Reset Password
                                </Button>
                            </Form.Item>
                        </Form>
                    )}

                    <div style={{textAlign: 'center', marginTop: '16px'}}>
                        <Button type="link" onClick={() => navigate('/')}>
                            Back to Home
                        </Button>
                    </div>
                </Card>
            </Content>
        </Layout>
    );
};

export default ResetPasswordPage;
